import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../contexts/AdminContext';
import AdminLayout from '../../components/admin/AdminLayout';
import ImageUpload from '../../components/admin/ImageUpload';
import { Plus, Edit, Trash2, Save, X, PlusCircle, MinusCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminEvents = () => {
  const { getAuthHeaders } = useAdmin();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/admin/events`, getAuthHeaders());
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
    setLoading(false);
  };

  const handleEdit = (event) => {
    setEditing(event.id);
    setFormData({
      ...event,
      agenda: event.agenda || [''],
      speakers: event.speakers || ['']
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Generate ID for new events
      const dataToSave = { ...formData };
      if (!dataToSave.id || dataToSave.id === 'new') {
        dataToSave.id = crypto.randomUUID();
      }
      
      if (editing === 'new') {
        await axios.post(`${API}/admin/events`, dataToSave, getAuthHeaders());
      } else {
        await axios.put(`${API}/admin/events/${formData.id}`, dataToSave, getAuthHeaders());
      }
      await fetchEvents();
      setEditing(null);
      setFormData({});
      alert('Event saved successfully!');
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Error saving event: ' + (error.response?.data?.detail || error.message));
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await axios.delete(`${API}/admin/events/${id}`, getAuthHeaders());
        await fetchEvents();
        alert('Event deleted successfully!');
      } catch (error) {
        console.error('Error deleting event:', error);
        alert('Error deleting event');
      }
    }
  };

  const handleNew = () => {
    setEditing('new');
    setFormData({
      id: '',
      name: '',
      type: 'Networking',
      date: '',
      teaser: '',
      fullDesc: '',
      price: 'Free',
      status: 'upcoming',
      icon: 'handshake',
      image: '',
      agenda: [''],
      speakers: [''],
      venue: ''
    });
  };

  const addAgendaItem = () => {
    setFormData({
      ...formData,
      agenda: [...(formData.agenda || []), '']
    });
  };

  const removeAgendaItem = (index) => {
    const newAgenda = formData.agenda.filter((_, i) => i !== index);
    setFormData({ ...formData, agenda: newAgenda.length ? newAgenda : [''] });
  };

  const updateAgendaItem = (index, value) => {
    const newAgenda = [...formData.agenda];
    newAgenda[index] = value;
    setFormData({ ...formData, agenda: newAgenda });
  };

  const addSpeaker = () => {
    setFormData({
      ...formData,
      speakers: [...(formData.speakers || []), '']
    });
  };

  const removeSpeaker = (index) => {
    const newSpeakers = formData.speakers.filter((_, i) => i !== index);
    setFormData({ ...formData, speakers: newSpeakers.length ? newSpeakers : [''] });
  };

  const updateSpeaker = (index, value) => {
    const newSpeakers = [...formData.speakers];
    newSpeakers[index] = value;
    setFormData({ ...formData, speakers: newSpeakers });
  };

  if (loading) return <AdminLayout><p className="text-white">Loading...</p></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="display-medium text-white">Events Management</h1>
          <button onClick={handleNew} className="btn-primary flex items-center gap-2" data-testid="add-event-btn">
            <Plus size={20} /> Add New Event
          </button>
        </div>

        {editing && (
          <div className="bg-white/10 border border-white/25 p-6 rounded-lg">
            <h2 className="heading-2 text-white mb-4">{editing === 'new' ? 'New Event' : 'Edit Event'}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="body-small text-white mb-1 block">Event Name *</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                  placeholder="e.g., AI Ethics Brunch"
                  data-testid="event-name-input"
                />
              </div>
              <div>
                <label className="body-small text-white mb-1 block">Type</label>
                <select
                  value={formData.type || 'Networking'}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                  data-testid="event-type-select"
                >
                  <option value="Networking">Networking</option>
                  <option value="Skills">Skills</option>
                  <option value="Hobbies">Hobbies</option>
                  <option value="Workshop">Workshop</option>
                  <option value="Meetup">Meetup</option>
                </select>
              </div>
              <div>
                <label className="body-small text-white mb-1 block">Date</label>
                <input
                  type="text"
                  value={formData.date || ''}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                  placeholder="e.g., Jan 25, 2026"
                  data-testid="event-date-input"
                />
              </div>
              <div>
                <label className="body-small text-white mb-1 block">Price</label>
                <input
                  type="text"
                  value={formData.price || ''}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                  placeholder="e.g., Free, ₹999"
                  data-testid="event-price-input"
                />
              </div>
              <div>
                <label className="body-small text-white mb-1 block">Status</label>
                <select
                  value={formData.status || 'upcoming'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                  data-testid="event-status-select"
                >
                  <option value="upcoming">Upcoming</option>
                  <option value="past">Past</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="body-small text-white mb-1 block">Icon</label>
                <select
                  value={formData.icon || 'handshake'}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                  data-testid="event-icon-select"
                >
                  <option value="handshake">Handshake (Networking)</option>
                  <option value="lightbulb">Lightbulb (Skills)</option>
                  <option value="palette">Palette (Creative)</option>
                  <option value="users">Users (Meetup)</option>
                  <option value="trophy">Trophy (Competition)</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="body-small text-white mb-1 block">Teaser (short description)</label>
                <input
                  type="text"
                  value={formData.teaser || ''}
                  onChange={(e) => setFormData({ ...formData, teaser: e.target.value })}
                  className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                  placeholder="Brief catchy description"
                  data-testid="event-teaser-input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="body-small text-white mb-1 block">Full Description</label>
                <textarea
                  value={formData.fullDesc || ''}
                  onChange={(e) => setFormData({ ...formData, fullDesc: e.target.value })}
                  className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded h-24"
                  placeholder="Detailed event description"
                  data-testid="event-desc-input"
                />
              </div>
              <div>
                <label className="body-small text-white mb-1 block">Venue</label>
                <input
                  type="text"
                  value={formData.venue || ''}
                  onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                  className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                  placeholder="e.g., Innovation Hub, Bangalore"
                  data-testid="event-venue-input"
                />
              </div>
              <div>
                <ImageUpload
                  label="Event Image"
                  value={formData.image}
                  onChange={(v) => setFormData({ ...formData, image: v })}
                  testId="event-image"
                />
              </div>
              
              {/* Agenda Section */}
              <div className="md:col-span-2">
                <div className="flex justify-between items-center mb-2">
                  <label className="body-small text-white">Agenda Items</label>
                  <button 
                    type="button"
                    onClick={addAgendaItem}
                    className="text-[#00FFD1] hover:text-[#6FD2C0] flex items-center gap-1 text-sm"
                  >
                    <PlusCircle size={16} /> Add Item
                  </button>
                </div>
                <div className="space-y-2">
                  {(formData.agenda || ['']).map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => updateAgendaItem(index, e.target.value)}
                        className="flex-1 bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                        placeholder="e.g., 6:00 PM - Registration & Networking"
                      />
                      {formData.agenda?.length > 1 && (
                        <button 
                          type="button"
                          onClick={() => removeAgendaItem(index)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <MinusCircle size={20} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Speakers Section */}
              <div className="md:col-span-2">
                <div className="flex justify-between items-center mb-2">
                  <label className="body-small text-white">Speakers</label>
                  <button 
                    type="button"
                    onClick={addSpeaker}
                    className="text-[#00FFD1] hover:text-[#6FD2C0] flex items-center gap-1 text-sm"
                  >
                    <PlusCircle size={16} /> Add Speaker
                  </button>
                </div>
                <div className="space-y-2">
                  {(formData.speakers || ['']).map((speaker, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={speaker}
                        onChange={(e) => updateSpeaker(index, e.target.value)}
                        className="flex-1 bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                        placeholder="e.g., Dr. Sarah Chen - AI Ethics Lead"
                      />
                      {formData.speakers?.length > 1 && (
                        <button 
                          type="button"
                          onClick={() => removeSpeaker(index)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <MinusCircle size={20} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button 
                onClick={handleSave} 
                disabled={saving}
                className="btn-primary flex items-center gap-2"
                data-testid="save-event-btn"
              >
                <Save size={20} /> {saving ? 'Saving...' : 'Save Event'}
              </button>
              <button 
                onClick={() => { setEditing(null); setFormData({}); }} 
                className="btn-secondary flex items-center gap-2"
                data-testid="cancel-event-btn"
              >
                <X size={20} /> Cancel
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4">
          {events.length === 0 ? (
            <div className="text-center py-12 bg-white/5 border border-white/25 rounded-lg">
              <p className="text-white/60 mb-4">No events yet</p>
              <button onClick={handleNew} className="btn-primary">Create Your First Event</button>
            </div>
          ) : (
            events.map((event) => (
              <div key={event.id} className="bg-white/5 border border-white/25 p-6 rounded-lg">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 text-xs rounded ${
                        event.status === 'upcoming' ? 'bg-green-500/20 text-green-400' :
                        event.status === 'past' ? 'bg-gray-500/20 text-gray-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {event.status}
                      </span>
                      <span className="text-white/40 text-xs">{event.type}</span>
                    </div>
                    <h3 className="heading-3 text-white mb-2">{event.name}</h3>
                    <p className="body-small text-white/85 mb-2">{event.teaser}</p>
                    <div className="flex flex-wrap gap-4 body-small text-white/60">
                      <span>📅 {event.date}</span>
                      <span>💰 {event.price}</span>
                      <span>📍 {event.venue}</span>
                    </div>
                    {event.speakers?.length > 0 && event.speakers[0] && (
                      <p className="body-small text-white/40 mt-2">
                        Speakers: {event.speakers.filter(s => s).join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button 
                      onClick={() => handleEdit(event)} 
                      className="text-[#00FFD1] hover:text-[#6FD2C0]"
                      data-testid={`edit-event-${event.id}`}
                    >
                      <Edit size={20} />
                    </button>
                    <button 
                      onClick={() => handleDelete(event.id)} 
                      className="text-red-500 hover:text-red-400"
                      data-testid={`delete-event-${event.id}`}
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminEvents;
